import { Box, Grow, IconButton, Slide, Snackbar, Typography, type SlideProps, type SnackbarCloseReason } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import CloseIcon from '@mui/icons-material/Close';

/**
 * The app's toast: top centre, drawn from the active theme.
 *
 * MUI's default Snackbar is a fixed light-grey bar in the bottom-left, which
 * reads as foreign on every one of the dark themes. This one sits on the
 * theme's paper colour with a tint and edge in the severity's colour, so it
 * follows whichever theme is picked instead of fixing a colour of its own.
 */

export type ToastSeverity = 'success' | 'error' | 'info' | 'warning';

export interface ToastMessage {
    readonly message: string;
    readonly severity: ToastSeverity;
}

interface ToastProps {
    readonly toast: ToastMessage | null;
    readonly onClose: (reason: SnackbarCloseReason | 'closeButton') => void;
    /** An extra button, such as Undo, shown before the close button. */
    readonly action?: React.ReactNode;
    readonly autoHideDuration?: number;
    /**
     * `centre` sits it in the middle of the screen. The employee app uses it:
     * on a phone the top edge is under the status bar and the notch, where
     * the message collided with the clock and battery.
     */
    readonly placement?: 'top' | 'centre';
}

const ICONS: Record<ToastSeverity, typeof CheckCircleRoundedIcon> = {
    success: CheckCircleRoundedIcon,
    error: ErrorRoundedIcon,
    info: InfoRoundedIcon,
    warning: WarningRoundedIcon,
};

function SlideDown(props: SlideProps): React.JSX.Element {
    return <Slide {...props} direction="down" />;
}

export function Toast({ toast, onClose, action, autoHideDuration = 4000, placement = 'top' }: ToastProps): React.JSX.Element {
    const theme = useTheme();
    const severity = toast?.severity ?? 'info';
    // Info follows the theme's own accent rather than MUI's stock blue.
    const colour = severity === 'info' ? theme.palette.primary.main : theme.palette[severity].main;
    const Icon = ICONS[severity];

    return (
        <Snackbar
            open={toast !== null}
            autoHideDuration={autoHideDuration}
            onClose={(_e, reason) => {
                onClose(reason);
            }}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            slots={{ transition: placement === 'centre' ? Grow : SlideDown }}
            sx={
                placement === 'centre'
                    ? {
                          // Doubled to outrank Snackbar's own breakpoint rule, which sets top: 24px.
                          '&&': {
                              top: '50%',
                              bottom: 'auto',
                              transform: { xs: 'translateY(-50%)', sm: 'translate(-50%, -50%)' },
                          },
                      }
                    : {}
            }
        >
            <Box
                role={severity === 'error' ? 'alert' : 'status'}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    minWidth: { xs: 'calc(100vw - 32px)', sm: 340 },
                    maxWidth: 520,
                    pl: 1.25,
                    pr: 0.75,
                    py: 1,
                    borderRadius: 2.5,
                    // Opaque paper under a translucent tint, so the board
                    // underneath never shows through.
                    bgcolor: 'background.paper',
                    backgroundImage: `linear-gradient(${alpha(colour, 0.16)}, ${alpha(colour, 0.16)})`,
                    border: '1px solid',
                    borderColor: alpha(colour, 0.45),
                    boxShadow: `0 10px 30px ${alpha(theme.palette.common.black, 0.45)}`,
                    backdropFilter: 'blur(6px)',
                }}
            >
                <Box
                    sx={{
                        flex: '0 0 auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: alpha(colour, 0.2),
                        color: colour,
                    }}
                >
                    <Icon sx={{ fontSize: 20 }} />
                </Box>
                <Typography sx={{ flex: 1, fontSize: '0.86rem', fontWeight: 600, color: 'text.primary' }}>
                    {toast?.message ?? ''}
                </Typography>
                {action}
                <IconButton
                    size="small"
                    aria-label="Dismiss"
                    onClick={() => {
                        onClose('closeButton');
                    }}
                    sx={{ color: 'text.secondary' }}
                >
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>
        </Snackbar>
    );
}
